/**
 * AgentenBriefingEditor — Admin-Bereich zum Ansehen und Bearbeiten der
 * Agenten-Konfiguration (Tabelle agenten_konfiguration).
 *
 * Der Inhaber kann Briefing, Ausführungs-Intervall, Aktiv-Status und die vier
 * darf_*-Rechte je Agent bearbeiten. Nur für Admins sichtbar (useAuth → isAdmin).
 *
 * Datenzugriff über /api/agent-briefing (GET Liste, POST Update) mit
 * Bearer-Token aus der Supabase-Session.
 */

import { useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import {
  Bot,
  Save,
  Loader2,
  ShieldAlert,
  RefreshCw,
  Mail,
  FileText,
  ListChecks,
  CreditCard,
} from 'lucide-react';

const BRAND = '#9B1C1C';

interface Agent {
  agent_id: string;
  name: string;
  briefing: string | null;
  ausfuehrung_intervall: string | null;
  bereiche: string[] | null;
  aktiv: boolean;
  briefing_version: number | null;
  darf_emails_senden: boolean;
  darf_dokumente_ablegen: boolean;
  darf_aufgaben_erstellen: boolean;
  darf_zahlungen_vorschlagen: boolean;
}

const INTERVALL_OPTIONEN: { value: string; label: string }[] = [
  { value: 'morgens', label: 'Morgens' },
  { value: 'zweimal_taeglich', label: 'Zweimal täglich' },
  { value: 'woechentlich', label: 'Wöchentlich' },
  { value: 'bei_bedarf', label: 'Bei Bedarf' },
];

const RECHTE: { key: keyof Agent; label: string; Icon: typeof Mail }[] = [
  { key: 'darf_emails_senden', label: 'E-Mails senden', Icon: Mail },
  { key: 'darf_dokumente_ablegen', label: 'Dokumente ablegen', Icon: FileText },
  { key: 'darf_aufgaben_erstellen', label: 'Aufgaben erstellen', Icon: ListChecks },
  { key: 'darf_zahlungen_vorschlagen', label: 'Zahlungen vorschlagen', Icon: CreditCard },
];

async function authHeader(): Promise<Record<string, string>> {
  const { data: { session } } = await supabase.auth.getSession();
  return session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {};
}

export default function AgentenBriefingEditor() {
  const { isAdmin } = useAuth();
  const [agenten, setAgenten] = useState<Agent[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const selected = useMemo(
    () => agenten.find((a) => a.agent_id === selectedId) || null,
    [agenten, selectedId],
  );

  async function laden() {
    setLoading(true);
    setFehler(null);
    try {
      const res = await fetch('/api/agent-briefing', {
        headers: { ...(await authHeader()) },
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json?.error || 'Laden fehlgeschlagen.');
      const liste: Agent[] = json.agenten || [];
      setAgenten(liste);
      setSelectedId((prev) => prev ?? liste[0]?.agent_id ?? null);
    } catch (err) {
      setFehler(err instanceof Error ? err.message : 'Laden fehlgeschlagen.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (isAdmin) laden();
    else setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  function patchSelected(patch: Partial<Agent>) {
    if (!selectedId) return;
    setAgenten((prev) =>
      prev.map((a) => (a.agent_id === selectedId ? { ...a, ...patch } : a)),
    );
  }

  async function speichern() {
    if (!selected) return;
    setSaving(true);
    try {
      const body = {
        agent_id: selected.agent_id,
        briefing: selected.briefing ?? '',
        ausfuehrung_intervall: selected.ausfuehrung_intervall ?? 'bei_bedarf',
        aktiv: selected.aktiv,
        darf_emails_senden: selected.darf_emails_senden,
        darf_dokumente_ablegen: selected.darf_dokumente_ablegen,
        darf_aufgaben_erstellen: selected.darf_aufgaben_erstellen,
        darf_zahlungen_vorschlagen: selected.darf_zahlungen_vorschlagen,
      };
      const res = await fetch('/api/agent-briefing', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(await authHeader()) },
        body: JSON.stringify(body),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json?.error || 'Speichern fehlgeschlagen.');
      if (json.agent) {
        setAgenten((prev) =>
          prev.map((a) => (a.agent_id === json.agent.agent_id ? json.agent : a)),
        );
      }
      toast.success('Gespeichert');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Speichern fehlgeschlagen.');
    } finally {
      setSaving(false);
    }
  }

  if (!isAdmin) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <ShieldAlert className="w-8 h-8 mb-3 text-gray-400" />
        <p className="text-sm text-gray-500">Dieser Bereich ist nur für den Inhaber.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin" style={{ color: BRAND }} />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6">
      <div className="flex items-center justify-between mb-6 gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <Bot className="w-6 h-6 shrink-0" style={{ color: BRAND }} />
          <div className="min-w-0">
            <h1 className="text-lg font-semibold text-gray-900 truncate">Agenten-Briefing</h1>
            <p className="text-xs text-gray-500">Briefings, Intervalle und Rechte verwalten</p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => laden()}
          className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-50"
        >
          <RefreshCw className="w-4 h-4" />
          <span className="hidden sm:inline">Neu laden</span>
        </button>
      </div>

      {fehler && (
        <div className="mb-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">{fehler}</div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
        {/* Agenten-Liste */}
        <div className="md:col-span-1">
          <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
            <div className="max-h-[70vh] overflow-y-auto divide-y divide-gray-100">
              {agenten.length === 0 && (
                <p className="p-4 text-sm text-gray-500">Keine Agenten gefunden.</p>
              )}
              {agenten.map((a) => {
                const aktiv = selectedId === a.agent_id;
                return (
                  <button
                    key={a.agent_id}
                    type="button"
                    onClick={() => setSelectedId(a.agent_id)}
                    className={`w-full text-left px-4 py-3 transition-colors ${
                      aktiv ? 'bg-red-50' : 'hover:bg-gray-50'
                    }`}
                    style={aktiv ? { boxShadow: `inset 3px 0 0 ${BRAND}` } : undefined}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium text-sm text-gray-900 truncate">{a.name}</span>
                      <span
                        className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium ${
                          a.aktiv ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                        }`}
                      >
                        {a.aktiv ? 'aktiv' : 'inaktiv'}
                      </span>
                    </div>
                    <p className="mt-0.5 text-xs text-gray-500 truncate">
                      {INTERVALL_OPTIONEN.find((o) => o.value === a.ausfuehrung_intervall)?.label ||
                        a.ausfuehrung_intervall ||
                        'kein Intervall'}
                    </p>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Editor */}
        <div className="md:col-span-2">
          {!selected ? (
            <div className="rounded-xl border border-dashed border-gray-200 p-10 text-center text-sm text-gray-500">
              Bitte einen Agenten auswählen.
            </div>
          ) : (
            <div className="rounded-xl border border-gray-200 bg-white p-4 sm:p-6 space-y-5">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <h2 className="font-semibold text-gray-900 truncate">{selected.name}</h2>
                  <p className="text-xs text-gray-400">
                    {selected.agent_id}
                    {typeof selected.briefing_version === 'number'
                      ? ` · Version ${selected.briefing_version}`
                      : ''}
                  </p>
                </div>
                <label className="flex items-center gap-2 shrink-0 cursor-pointer">
                  <span className="text-sm text-gray-600">Aktiv</span>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={selected.aktiv}
                    onClick={() => patchSelected({ aktiv: !selected.aktiv })}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      selected.aktiv ? '' : 'bg-gray-200'
                    }`}
                    style={selected.aktiv ? { backgroundColor: BRAND } : undefined}
                  >
                    <span
                      className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
                        selected.aktiv ? 'translate-x-5' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </label>
              </div>

              {/* Briefing */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Briefing</label>
                <textarea
                  value={selected.briefing ?? ''}
                  onChange={(e) => patchSelected({ briefing: e.target.value })}
                  rows={10}
                  placeholder="Briefing / Anweisungen für diesen Agenten…"
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-red-100 focus:border-red-300 resize-y"
                />
              </div>

              {/* Intervall */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Ausführungs-Intervall
                </label>
                <select
                  value={selected.ausfuehrung_intervall ?? 'bei_bedarf'}
                  onChange={(e) => patchSelected({ ausfuehrung_intervall: e.target.value })}
                  className="w-full sm:w-64 rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900 bg-white focus:outline-none focus:ring-2 focus:ring-red-100 focus:border-red-300"
                >
                  {INTERVALL_OPTIONEN.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Rechte */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Rechte</label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {RECHTE.map(({ key, label, Icon }) => {
                    const checked = Boolean(selected[key]);
                    return (
                      <label
                        key={String(key)}
                        className="flex items-center gap-3 rounded-lg border border-gray-200 px-3 py-2.5 cursor-pointer hover:bg-gray-50"
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) => patchSelected({ [key]: e.target.checked } as Partial<Agent>)}
                          className="h-4 w-4 rounded border-gray-300"
                          style={{ accentColor: BRAND }}
                        />
                        <Icon className="w-4 h-4 text-gray-400 shrink-0" />
                        <span className="text-sm text-gray-700">{label}</span>
                      </label>
                    );
                  })}
                </div>
              </div>

              <div className="flex justify-end pt-2">
                <button
                  type="button"
                  onClick={() => speichern()}
                  disabled={saving}
                  className="inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium text-white disabled:opacity-60"
                  style={{ backgroundColor: BRAND }}
                >
                  {saving ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                  Speichern
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
